import type { ReactNode } from "react";

/* ─────────────────────────── Top navigation ─────────────────────────── */

export function TopNav() {
  return (
    <header className="sticky top-0 z-20 h-14 w-full bg-sixt-black text-white">
      <div className="mx-auto flex h-full max-w-[1280px] items-center justify-between px-6">
        <div className="flex items-center gap-8">
          <Wordmark />
          <nav className="hidden items-center gap-6 text-sm md:flex">
            <NavLink active>Inspections</NavLink>
          </nav>
        </div>
        <div className="flex items-center gap-5 text-sm">
          <div className="flex items-center gap-2">
            <div className="grid h-7 w-7 place-items-center rounded-full bg-accent text-xs font-bold">
              A
            </div>
            <span className="hidden md:inline">Admin</span>
          </div>
        </div>
      </div>
    </header>
  );
}

function Wordmark() {
  return (
    <div className="flex items-baseline gap-1">
      <span className="text-[13px] font-semibold uppercase tracking-[0.18em] text-white">
        Damage Inspection
      </span>
    </div>
  );
}

function NavLink({
  children,
  active,
}: {
  children: ReactNode;
  active?: boolean;
}) {
  return (
    <a
      href="#"
      className={
        "relative py-1 transition-colors " +
        (active
          ? "text-white after:absolute after:-bottom-[18px] after:left-0 after:h-[3px] after:w-full after:bg-accent"
          : "text-white/70 hover:text-white")
      }
    >
      {children}
    </a>
  );
}

/* ─────────────────────────── Vehicle context ─────────────────────────── */

export function VehicleContextCard({
  vehicle,
  rego,
}: {
  vehicle?: string;
  rego?: string;
}) {
  return (
    <div className="mt-6 grid grid-cols-1 gap-px overflow-hidden rounded-card border border-border-grey bg-border-grey md:grid-cols-2">
      <ContextCell
        label="Vehicle"
        value={vehicle ?? "—"}
        sub={vehicle ? "Detected" : "Not detected"}
      />
      <ContextCell
        label="Rego"
        value={rego ?? "—"}
        sub={rego ? "Detected" : "Not detected"}
      />
    </div>
  );
}

function ContextCell({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <div className="bg-white px-5 py-4">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-text-secondary">
        {label}
      </div>
      <div className="mt-1 text-sm font-semibold">{value}</div>
      {sub && <div className="text-xs text-text-secondary">{sub}</div>}
    </div>
  );
}

/* ─────────────────────────── Breadcrumb ─────────────────────────── */

export function Breadcrumb({ trail }: { trail: string[] }) {
  return (
    <nav className="flex items-center gap-2 text-xs text-text-secondary">
      {trail.map((item, i) => {
        const last = i === trail.length - 1;
        return (
          <span key={i} className="flex items-center gap-2">
            {i > 0 && <ChevronRight />}
            <span className={last ? "text-sixt-black" : ""}>{item}</span>
          </span>
        );
      })}
    </nav>
  );
}

/* ─────────────────────────── Footer ─────────────────────────── */

export function Footer() {
  return (
    <footer className="mt-16 border-t border-border-grey bg-white">
      <div className="mx-auto flex max-w-[1280px] flex-wrap items-center justify-between gap-4 px-6 py-6 text-xs text-text-secondary">
        <div className="flex items-center gap-2">
          <span>Internal tool · v0.1 prototype</span>
        </div>
        <div className="flex items-center gap-5">
          <a href="#" className="hover:text-sixt-black">
            Documentation
          </a>
          <a href="#" className="hover:text-sixt-black">
            Report a bug
          </a>
          <a href="#" className="hover:text-sixt-black">
            Privacy
          </a>
        </div>
      </div>
    </footer>
  );
}

/* ─────────────────────────── Icons ─────────────────────────── */

export function ChevronRight() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
      <path
        d="M9 6l6 6-6 6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function ArrowRight() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
      <path
        d="M5 12h14M13 5l7 7-7 7"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function UploadIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
      <path
        d="M12 16V4m0 0l-5 5m5-5l5 5M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function ShieldIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
      <path
        d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function MoreIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <circle cx="5" cy="12" r="1.6" />
      <circle cx="12" cy="12" r="1.6" />
      <circle cx="19" cy="12" r="1.6" />
    </svg>
  );
}

export function CloseIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
      <path
        d="M6 6l12 12M18 6L6 18"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
      <path
        d="M5 12l5 5L20 7"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function CameraIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <path
        d="M4 8h3l2-3h6l2 3h3a1 1 0 011 1v10a1 1 0 01-1 1H4a1 1 0 01-1-1V9a1 1 0 011-1z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <circle
        cx="12"
        cy="13"
        r="3.5"
        stroke="currentColor"
        strokeWidth="1.8"
      />
    </svg>
  );
}

export function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
